import type { ChangeEvent } from "react";
import styles from "./RangeField.module.css";

export interface RangeFieldValue {
  min: number;
  max: number;
}

export interface RangeFieldProps {
  /** The two ends. A non-finite end (NaN) is a box the operator has cleared,
   *  and draws empty rather than as «NaN». */
  value: RangeFieldValue;
  /** Called with BOTH ends whenever either is typed in; a cleared box is NaN. */
  onChange: (next: RangeFieldValue) => void;
  /**
   * What the range is. Names the group, and each number as «<label>, from» /
   * «<label>, to» — two boxes that look alike are told apart only by this.
   */
  label: string;
  /** The unit written after the second number («days», «min»). */
  unit?: string;
  /** Paints the whole box red; the reason is printed by the caller, once. */
  error?: boolean;
  min?: number;
  max?: number;
  className?: string;
}

/**
 * A min–max pair as ONE control: a single bordered box holding both numbers,
 * an en dash and the unit, so a table column holds one field and reads as one
 * value. Focus and error belong to the box rather than to either number,
 * because a backwards range is a fault of the pair, not of one end.
 */
export function RangeField({
  value,
  onChange,
  label,
  unit,
  error,
  min,
  max,
  className,
}: RangeFieldProps) {
  const read = (e: ChangeEvent<HTMLInputElement>) =>
    e.target.value === "" ? NaN : Number(e.target.value);
  const shown = (n: number) => (Number.isFinite(n) ? String(n) : "");
  const cls = [styles.root, error ? styles.error : "", className ?? ""]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cls} role="group" aria-label={label}>
      <input
        className={styles.input}
        type="number"
        value={shown(value.min)}
        min={min}
        max={max}
        onChange={(e) => onChange({ ...value, min: read(e) })}
        aria-label={`${label}, from`}
        aria-invalid={error || undefined}
      />
      <span className={styles.dash} aria-hidden>
        –
      </span>
      <input
        className={styles.input}
        type="number"
        value={shown(value.max)}
        min={min}
        max={max}
        onChange={(e) => onChange({ ...value, max: read(e) })}
        aria-label={`${label}, to`}
        aria-invalid={error || undefined}
      />
      <span className={styles.unit}>{unit}</span>
    </div>
  );
}
